import React, { useState } from 'react';
import { Box, Stack } from '@mui/material';
import { motion } from 'framer-motion';
import DetailModal from './DetailModal';

// =====================================================================
// Real KBT weekly schedule — from the Parshat Nitzavim-Vayelech bulletin
// (September 4, 2026). Times are the primary visual element of each row.
// =====================================================================

const COLUMNS = [
  {
    head: 'Erev Shabbat',
    sub: 'Friday',
    accent: 'var(--teal-500)',
    items: [
      { time: '6:15 PM', title: 'Shir Hashirim',  hebrew: 'שִׁיר הַשִּׁירִים', note: 'Song of Songs · before Mincha' },
      { time: '6:30 PM', title: 'Mincha · Arvit', hebrew: 'מִנְחָה / עֲרְבִית', note: 'Friday afternoon & Maariv', highlight: true },
      { time: '6:57 PM', title: 'Candle Lighting', hebrew: 'הַדְלָקַת נֵרוֹת', note: '18 min before sunset · at home' },
      { time: '7:15 PM', title: 'Shkiyah',         hebrew: 'שְׁקִיעָה',            note: 'Sunset' },
    ],
  },
  {
    head: 'Shabbat Morning',
    sub: 'Saturday',
    accent: 'var(--navy-500)',
    items: [
      { time: '8:15 AM', title: 'Hilchot Shabbat', hebrew: 'הִלְכוֹת שַׁבָּת', note: 'Pre-davening shiur' },
      { time: '8:45 AM', title: 'Hodu',             hebrew: 'הוֹדוּ',               note: 'Morning service begins · followed by Kiddush' },
      { time: '9:02 AM', title: "Shema Mg\"A",     hebrew: 'שְׁמַע מ"א',         note: 'Latest time per Magen Avraham' },
      { time: '9:40 AM', title: 'Gr"A',             hebrew: 'גְּר"א',               note: 'Latest Shema per the Gr"a' },
      { time: 'After',   title: 'Ben Ish Chai Shiur', hebrew: 'בֶּן אִישׁ חַי',   note: 'With Rabbi Dror Baalhaness · after Kiddush' },
    ],
  },
  {
    head: 'Shabbat Afternoon',
    sub: 'Saturday',
    accent: 'var(--gold-main)',
    items: [
      { time: '5:25 PM', title: 'Boys Program · Shiur', hebrew: 'שִׁעוּר',         note: 'Boys program & afternoon shiur' },
      { time: '6:25 PM', title: 'Mincha',                hebrew: 'מִנְחָה',          note: 'Followed by Seudah Sh’lishit', highlight: true },
      { time: '7:13 PM', title: 'Shkiyah',               hebrew: 'שְׁקִיעָה',         note: 'Sunset' },
      { time: '7:50 PM', title: 'Arvit',                  hebrew: 'עֲרְבִית',          note: 'Maariv' },
      { time: '7:52 PM', title: 'Shabbat Ends',           hebrew: 'מוֹצָאֵי שַׁבָּת',  note: 'Havdalah' },
      { time: '8:26 PM', title: 'Rabbeinu Tam',           hebrew: 'רַבֵּינוּ תָּם',    note: 'Stringent zman' },
    ],
  },
  {
    head: 'Sunday',
    sub: 'Weekly',
    accent: 'var(--teal-700)',
    items: [
      { time: '7:30 AM', title: 'Selichot',       hebrew: 'סְלִיחוֹת',         note: 'Penitential prayers' },
      { time: '8:15 AM', title: 'Shacharit',      hebrew: 'שַׁחֲרִית',         note: 'Morning prayer' },
      { time: '6:50 PM', title: 'Mincha · Arvit', hebrew: 'מִנְחָה / עֲרְבִית', note: 'Afternoon & evening' },
    ],
  },
  {
    head: 'Monday — Friday',
    sub: 'Weekday',
    accent: 'var(--navy-500)',
    items: [
      { time: '6:00 AM', title: 'Selichot',        hebrew: 'סְלִיחוֹת',         note: 'Early selichot' },
      { time: '6:45 AM', title: 'Shacharit',       hebrew: 'שַׁחֲרִית',         note: 'Early minyan' },
      { time: '7:40 AM', title: '2nd Selichot',    hebrew: 'סְלִיחוֹת שֵׁנִי',  note: 'Late selichot' },
      { time: '8:25 AM', title: '2nd Shacharit',   hebrew: 'שַׁחֲרִית שֵׁנִי',   note: 'Late minyan' },
      { time: '6:50 PM', title: 'Mincha · Arvit',  hebrew: 'מִנְחָה / עֲרְבִית', note: 'Afternoon & evening' },
    ],
  },
];

// Shiurim block — a separate sub-section below the main schedule.
const SHIURIM = [
  {
    teacher: 'Shiurim by Rabbi Yisraeli',
    items: [
      { when: 'Sundays',         what: 'Hilchot Shabbat', time: '7:00 AM' },
      { when: 'Mon — Fri',       what: 'Amud Yomi',        time: '6:00 AM' },
      { when: 'Mon — Fri',       what: 'Mishna Berura',    time: '7:30 AM' },
      { when: 'Mon — Fri',       what: 'Amud Yomi',        time: '9:00 AM' },
      { when: 'Mon — Thu',       what: 'Hilchot Yichud',   time: '8:30 PM' },
    ],
  },
  {
    teacher: 'Shiurim by Rabbi Baalhaness',
    items: [
      { when: 'Mon & Wed',       what: "Halacha B'Iyun",   time: '8:30 PM' },
      { when: 'Tue nights',      what: 'Chinuch Yeladim',  time: '8:30 PM' },
      { when: 'Thu nights',      what: 'Parsha & Chollent', time: '8:30 PM' },
    ],
  },
  {
    teacher: 'Shiurim by Rabbi Rashti',
    items: [
      { when: 'Mon — Fri',       what: 'Amud Yomi',        time: '7:30 — 8:05 AM' },
      { when: 'Semichas Chaver', what: 'Track 1 — Mon',    time: '8:00 – 9:30 PM' },
      { when: 'Semichas Chaver', what: 'Track 2 — Tue',    time: '8:00 – 9:30 PM' },
      { when: 'Semichas Chaver', what: 'Track 3 — Wed',    time: '8:00 – 9:30 PM' },
      { when: 'Semichas Chaver', what: 'Track 4 — Thu',    time: '8:00 – 9:30 PM' },
      { when: 'Semichas Chaver', what: 'Track 5 — Shabbat afternoon (1 hr before Mincha)', time: '—' },
      { when: 'Semichas Chaver', what: 'Track 6 — Sun',    time: '9:15 – 10:30 AM' },
    ],
  },
  {
    teacher: 'Shiur by Dr. Youssefzadeh',
    items: [
      { when: 'Wed nights',      what: 'Parasha Mastery',  time: '8:30 PM' },
    ],
  },
];

function ScheduleRow({ row, accent, columnHead, onOpen }) {
  return (
    <Box
      onClick={() => onOpen({ row, accent, columnHead })}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen({ row, accent, columnHead });
        }
      }}
      sx={{
        cursor: 'pointer',
        position: 'relative',
        py: { xs: 2.25, md: 2.5 },
        px: 0.5,
        borderTop: '1px solid rgba(36,34,88,0.08)',
        transition: 'background 250ms ease, padding-left 250ms ease',
        '&:hover': {
          background: row.highlight
            ? 'rgba(212,178,107,0.10)'
            : 'rgba(15,117,188,0.05)',
          paddingLeft: '12px',
        },
        '&:hover .row-expand': { opacity: 1, transform: 'translateX(0)' },
        '&:focus-visible': {
          outline: '2px solid var(--teal-500)',
          outlineOffset: 2,
        },
      }}
    >
      <Stack direction="row" alignItems="baseline" spacing={1.5} sx={{ mb: 0.75 }}>
        {/* BIG, prominent time — the visual anchor of each row */}
        <Box
          sx={{
            fontFamily: 'var(--font-display)',
            fontWeight: 800,
            fontSize: 'clamp(28px, 3.4vw, 38px)',
            lineHeight: 1,
            letterSpacing: '-0.02em',
            color: row.highlight ? accent : 'var(--navy-500)',
            whiteSpace: 'nowrap',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {row.time}
        </Box>

        <Box className="row-expand" sx={{
          ml: 'auto',
          fontFamily: 'var(--font-display)',
          fontSize: 20,
          color: accent,
          opacity: 0,
          transform: 'translateX(-6px)',
          transition: 'opacity 250ms ease, transform 250ms ease',
          lineHeight: 1,
        }}>↗</Box>
      </Stack>

      <Stack direction="row" alignItems="baseline" spacing={1.5} flexWrap="wrap">
        <Box
          sx={{
            fontFamily: 'var(--font-display)',
            fontWeight: 700,
            fontSize: 17,
            color: 'var(--navy-500)',
            lineHeight: 1.2,
          }}
        >
          {row.title}
        </Box>
        <Box
          sx={{
            fontFamily: 'var(--font-hebrew)',
            fontSize: 16,
            color: accent,
            lineHeight: 1.1,
            direction: 'rtl',
          }}
        >
          {row.hebrew}
        </Box>
      </Stack>
      <Box
        sx={{
          mt: 0.5,
          fontFamily: 'var(--font-body)',
          fontSize: 13.5,
          color: 'var(--text-muted)',
          lineHeight: 1.4,
        }}
      >
        {row.note}
      </Box>
    </Box>
  );
}

function ScheduleColumn({ column, index, onOpen }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-10% 0%' }}
      transition={{ duration: 0.9, delay: 0.08 * index, ease: [0.22, 1, 0.36, 1] }}
      style={{ display: 'flex' }}
    >
      <Box
        sx={{
          flex: 1,
          background: '#fff',
          border: '1px solid rgba(36,34,88,0.10)',
          p: { xs: 3, md: 3.5 },
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          overflow: 'hidden',
          transition: 'border-color 350ms ease, transform 350ms ease, box-shadow 350ms ease',
          '&:hover': {
            borderColor: column.accent,
            transform: 'translateY(-3px)',
            boxShadow: '0 16px 36px -20px rgba(36,34,88,0.28)',
          },
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            background: column.accent,
          }}
        />

        <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 2 }}>
          <Box>
            <Box
              sx={{
                fontFamily: 'var(--font-mono)',
                fontSize: 10,
                letterSpacing: '0.22em',
                color: 'var(--text-muted)',
                textTransform: 'uppercase',
                mb: 0.75,
              }}
            >
              {column.sub}
            </Box>
            <Box
              sx={{
                fontFamily: 'var(--font-display)',
                fontWeight: 700,
                fontSize: 24,
                color: 'var(--navy-500)',
                lineHeight: 1.05,
                letterSpacing: '-0.01em',
              }}
            >
              {column.head}
            </Box>
          </Box>
          <Box
            sx={{
              fontFamily: 'var(--font-mono)',
              fontSize: 10,
              letterSpacing: '0.22em',
              color: 'var(--text-soft)',
              textTransform: 'uppercase',
            }}
          >
            {String(column.items.length).padStart(2, '0')}
          </Box>
        </Stack>

        <Box sx={{ flex: 1 }}>
          {column.items.map((row, i) => (
            <ScheduleRow
              key={i}
              row={row}
              accent={column.accent}
              columnHead={column.head}
              onOpen={onOpen}
            />
          ))}
        </Box>
      </Box>
    </motion.div>
  );
}

function ShiurimBlock({ block, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-10% 0%' }}
      transition={{ duration: 0.9, delay: 0.1 * index, ease: [0.22, 1, 0.36, 1] }}
    >
      <Box
        sx={{
          background: 'var(--cream-50)',
          border: '1px solid rgba(36,34,88,0.10)',
          borderTop: '3px solid var(--gold-main)',
          p: { xs: 3, md: 3.5 },
          height: '100%',
        }}
      >
        <Box
          sx={{
            fontFamily: 'var(--font-display)',
            fontWeight: 700,
            fontStyle: 'italic',
            fontSize: 20,
            color: 'var(--navy-500)',
            mb: 2.5,
            letterSpacing: '-0.005em',
          }}
        >
          {block.teacher}
        </Box>

        <Stack divider={<Box sx={{ height: '1px', background: 'rgba(36,34,88,0.08)' }} />}>
          {block.items.map((item, i) => (
            <Box key={i} sx={{ py: 1.5 }}>
              <Box
                sx={{
                  fontFamily: 'var(--font-display)',
                  fontWeight: 800,
                  fontSize: 'clamp(20px, 2vw, 26px)',
                  color: 'var(--navy-500)',
                  lineHeight: 1.0,
                  letterSpacing: '-0.01em',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {item.time}
              </Box>
              <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mt: 0.5 }}>
                <Box
                  sx={{
                    fontFamily: 'var(--font-body)',
                    fontWeight: 600,
                    fontSize: 14,
                    color: 'var(--navy-500)',
                  }}
                >
                  {item.what}
                </Box>
                <Box
                  sx={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: 10,
                    letterSpacing: '0.22em',
                    textTransform: 'uppercase',
                    color: 'var(--text-muted)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {item.when}
                </Box>
              </Stack>
            </Box>
          ))}
        </Stack>
      </Box>
    </motion.div>
  );
}

export default function ScheduleAtAGlance() {
  const [openRow, setOpenRow] = useState(null);

  return (
    <Box
      component="section"
      id="schedule"
      sx={{
        position: 'relative',
        background: 'var(--cream-50)',
        py: { xs: 10, md: 16 },
        px: { xs: 3, md: 6 },
        overflow: 'hidden',
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          left: '-4%',
          bottom: '-20%',
          fontFamily: 'var(--font-hebrew)',
          fontSize: 'min(60vw, 720px)',
          color: 'var(--navy-500)',
          opacity: 0.04,
          lineHeight: 0.85,
          pointerEvents: 'none',
          userSelect: 'none',
        }}
      >
        ת
      </Box>

      <Box sx={{ position: 'relative', maxWidth: 1440, mx: 'auto' }}>
        {/* Section header */}
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'flex-end' }}
          spacing={3}
          sx={{ mb: { xs: 5, md: 8 } }}
        >
          <Box sx={{ maxWidth: 760 }}>
            <Box className="kicker" sx={{ mb: 2 }}>
              Parshat Nitzavim-Vayelech  ·  September 4, 2026
            </Box>
            <motion.h2
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-10% 0%' }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              style={{
                margin: 0,
                fontFamily: 'var(--font-display)',
                fontWeight: 700,
                fontSize: 'clamp(36px, 5.4vw, 80px)',
                lineHeight: 0.98,
                letterSpacing: '-0.02em',
                color: 'var(--navy-500)',
              }}
            >
              The whole schedule,
              <br />
              <span style={{ fontWeight: 500, fontStyle: 'italic', color: 'var(--teal-500)' }}>
                at a glance.
              </span>
            </motion.h2>
          </Box>

          <Box
            sx={{
              fontFamily: 'var(--font-display)',
              fontStyle: 'italic',
              fontWeight: 500,
              fontSize: 'clamp(15px, 1.4vw, 18px)',
              color: 'var(--text-muted)',
              lineHeight: 1.5,
              maxWidth: 360,
            }}
          >
            Click any row for the full details. Times shift weekly with the
            parsha — check before Shabbat.
          </Box>
        </Stack>

        {/* Sponsorship/Kids strip */}
        <Box
          sx={{
            mb: { xs: 4, md: 5 },
            p: 2.5,
            background: '#fff',
            border: '1px solid rgba(36,34,88,0.10)',
            borderLeft: '4px solid var(--gold-main)',
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: 2.5,
          }}
        >
          {[
            {
              label: 'Shabbat Kiddush',
              value: 'Available for Sponsorship',
            },
            {
              label: "Seudah Sh'lishit",
              value: 'Available for Sponsorship',
            },
            { label: 'Kids · Parsha Puppets', value: '11:00 AM  ·  Shabbat Morning' },
          ].map((item) => (
            <Box key={item.label}>
              <Box
                sx={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 10,
                  letterSpacing: '0.22em',
                  textTransform: 'uppercase',
                  color: 'var(--gold-dark)',
                  mb: 0.5,
                }}
              >
                {item.label}
              </Box>
              <Box
                sx={{
                  fontFamily: 'var(--font-display)',
                  fontWeight: 500,
                  fontStyle: 'italic',
                  fontSize: 16,
                  color: 'var(--navy-500)',
                }}
              >
                {item.value}
              </Box>
            </Box>
          ))}
        </Box>

        {/* Tefilah grid */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              lg: 'repeat(5, 1fr)',
            },
            gap: { xs: 2.5, md: 3 },
            alignItems: 'stretch',
            mb: { xs: 6, md: 9 },
          }}
        >
          {COLUMNS.map((col, i) => (
            <ScheduleColumn key={col.head} column={col} index={i} onOpen={setOpenRow} />
          ))}
        </Box>

        {/* Shiurim sub-section */}
        <Box>
          <Box className="kicker" sx={{ mb: 2 }}>Shiurim  ·  Weekly</Box>
          <motion.h3
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-10% 0%' }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            style={{
              margin: '0 0 32px',
              fontFamily: 'var(--font-display)',
              fontWeight: 700,
              fontSize: 'clamp(28px, 3.4vw, 48px)',
              lineHeight: 1,
              letterSpacing: '-0.015em',
              color: 'var(--navy-500)',
            }}
          >
            Classes,
            <span style={{ fontWeight: 500, fontStyle: 'italic', color: 'var(--gold-dark)' }}>
              {' '}by teacher.
            </span>
          </motion.h3>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: { xs: 2.5, md: 3 },
              alignItems: 'stretch',
            }}
          >
            {SHIURIM.map((block, i) => (
              <ShiurimBlock key={block.teacher} block={block} index={i} />
            ))}
          </Box>
        </Box>

        {/* Footer rail */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={3}
          sx={{ mt: { xs: 6, md: 8 } }}
        >
          <Box
            sx={{
              fontFamily: 'var(--font-mono)',
              fontSize: 10.5,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: 'var(--text-muted)',
            }}
          >
            All times PT  ·  9017 W. Pico Blvd
          </Box>

        </Stack>
      </Box>

      <DetailModal
        open={Boolean(openRow)}
        onClose={() => setOpenRow(null)}
        accent={openRow?.accent}
        kicker={openRow ? `${openRow.columnHead}  ·  ${openRow.row.time}` : ''}
        hebrew={openRow?.row.hebrew}
        title={openRow?.row.title}
        subtitle={openRow?.row.note}
        time={openRow?.row.time}
        body={[
          'KBT  ·  Kehilat Bnei Torah, 9017 W. Pico Blvd, Los Angeles.',
          'Walk-in seats every week. Times shift slightly with the season — check the live page before Shabbat.',
        ]}
        meta={openRow ? [
          { label: 'Time', value: openRow.row.time },
          { label: 'Day',  value: openRow.columnHead },
          { label: 'Hebrew', value: openRow.row.hebrew },
          { label: 'Location', value: 'KBT  ·  9017 W. Pico Blvd' },
        ] : []}
      />
    </Box>
  );
}
